import { useQuery } from '@tanstack/react-query'
import { createFileRoute, getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  asProfileRole,
  profileQueries,
  profileUpdatedAt,
  type ProfileListItem,
} from '@/api/endpoints/profiles'
import { toProfileListParams } from '@/features/profiles/profile-list-search'
import { CountryFlag } from '@/components/kit/country-flag'
import { DataTable } from '@/components/kit/data-table'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiErrorMessage } from '@/api/errors'
import { countryName } from '@/lib/countries'
import { formatDisplayDateTime } from '@/lib/dates'
import { searchDraftForUrl } from '@/lib/list-search'
import { playerPositionLabel, staffPositionLabel } from '@/lib/profile-roles'
import { SERVER_OPTIONS } from '@/lib/servers'

const profilesRoute = getRouteApi('/_authed/profiles')

export const Route = createFileRoute('/_authed/profiles/')({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.prefetchQuery(profileQueries.list(toProfileListParams(deps))),
  component: ProfilesPage,
})

const columns: ColumnDef<ProfileListItem>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <Link
        to="/profiles/$uuid"
        params={{ uuid: row.original.uuid! }}
        search
        className="font-medium text-primary underline-offset-4 hover:underline"
      >
        {row.original.name}
      </Link>
    ),
  },
  {
    accessorKey: 'country',
    header: 'Country',
    cell: ({ row }) => (
      <span className="flex items-center gap-2">
        <CountryFlag code={row.original.country ?? ''} />
        {countryName(row.original.country ?? '')}
      </span>
    ),
  },
  {
    id: 'roles',
    header: 'Roles',
    cell: ({ row }) => {
      const player = asProfileRole(row.original.league_player)?.position
      const staff = asProfileRole(row.original.staff)?.position
      if (!player && !staff) return '—'
      return (
        <span className="text-muted-foreground">
          {[player ? playerPositionLabel(player) : null, staff ? staffPositionLabel(staff) : null]
            .filter(Boolean)
            .join(' · ')}
        </span>
      )
    },
  },
  {
    id: 'updated_at',
    header: 'Updated',
    cell: ({ row }) => {
      const updatedAt = profileUpdatedAt(row.original)
      return formatDisplayDateTime(updatedAt)
    },
  },
]

function ProfilesPage() {
  const search = profilesRoute.useSearch()
  const navigate = useNavigate({ from: '/profiles' })
  const { data, isLoading, isFetching, isPlaceholderData, isError, error } = useQuery(
    profileQueries.list(toProfileListParams(search)),
  )
  const [draft, setDraft] = useState(search.query ?? '')
  const submittedRef = useRef(search.query ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    [],
  )

  useEffect(() => {
    const nextDraft = searchDraftForUrl(search.query, submittedRef.current)
    if (nextDraft === undefined) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    submittedRef.current = nextDraft
    setDraft(nextDraft)
  }, [search.query])

  const pagination = useMemo(
    () => ({
      page: search.page,
      displayPage: isPlaceholderData ? (data?.current ?? search.page) : search.page,
      pageSize: search.page_size,
      pages: data?.pages ?? 0,
      total: data?.total ?? 0,
      onPageChange: (page: number) => void navigate({ search: (prev) => ({ ...prev, page }) }),
      onPageSizeChange: (page_size: number) =>
        void navigate({ search: (prev) => ({ ...prev, page_size, page: 1 }) }),
    }),
    [data, isPlaceholderData, search.page, search.page_size, navigate],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-2xl font-semibold">Profiles</h1>
        {isFetching && !isLoading && (
          <p className="text-sm text-muted-foreground" aria-live="polite">
            Updating…
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        <Input
          className="max-w-sm"
          placeholder="Search…"
          value={draft}
          aria-label="Search profiles"
          onChange={(e) => {
            const value = e.target.value
            setDraft(value)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
              submittedRef.current = value
              void navigate({
                search: (prev) => ({ ...prev, query: value || undefined, page: 1 }),
                replace: true,
              })
            }, 300)
          }}
        />
        <Select
          disabled={search.without_accounts === true}
          value={search.without_accounts === true ? 'all' : (search.server ?? 'all')}
          onValueChange={(value) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                server: value === 'all' ? undefined : (value as typeof search.server),
                page: 1,
              }),
            })
          }
        >
          <SelectTrigger className="w-40" aria-label="Filter by server">
            <SelectValue placeholder="Server" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All servers</SelectItem>
            {SERVER_OPTIONS.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Checkbox
            id="profiles-without-accounts"
            checked={search.without_accounts === true}
            onCheckedChange={(checked) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  without_accounts: checked === true ? true : undefined,
                  page: 1,
                }),
              })
            }
          />
          <Label htmlFor="profiles-without-accounts">League players without Riot accounts</Label>
        </div>
        {(search.query || search.server || search.without_accounts) && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              if (debounceRef.current) clearTimeout(debounceRef.current)
              submittedRef.current = ''
              setDraft('')
              void navigate({
                search: (prev) => ({
                  ...prev,
                  query: undefined,
                  server: undefined,
                  without_accounts: undefined,
                  page: 1,
                }),
              })
            }}
          >
            Clear filters
          </Button>
        )}
      </div>
      <DataTable
        columns={columns}
        data={isError ? [] : (data?.results ?? [])}
        getRowId={(profile) => profile.uuid ?? ''}
        pagination={pagination}
        isLoading={isLoading}
        errorMessage={isError ? apiErrorMessage(error) : undefined}
      />
    </div>
  )
}
